import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import type { AdminCapability } from '@kopechka/contracts';
import { useSession } from '../session/session-context.js';
import { Icon } from '../ui/index.js';
import type { IconName } from '../ui/index.js';

interface NavItem {
  readonly path: string;
  readonly label: string;
  readonly capability: AdminCapability | null;
  readonly icon: IconName;
}

interface NavSection {
  readonly title: string | null;
  readonly items: readonly NavItem[];
  /**
   * When true, the section is collapsed by default. Reserved for
   * deep operational menus; no current section uses this since the
   * R22.1 Operasyon menu was removed (D-038).
   */
  readonly collapsibleByDefault?: boolean;
}

const SECTIONS: readonly NavSection[] = [
  {
    title: 'Genel',
    items: [{ path: '/', label: 'Genel Bakış', capability: null, icon: 'Activity' }],
  },
  {
    title: 'Müşteri',
    items: [
      { path: '/users', label: 'Kullanıcılar', capability: 'user:view_detail', icon: 'Users' },
      { path: '/messages', label: 'Mesajlar', capability: 'chat:view', icon: 'Mail' },
      { path: '/orders', label: 'Siparişler', capability: 'order:inspect', icon: 'Inbox' },
    ],
  },
  {
    title: 'Sağlayıcılar',
    items: [
      { path: '/mailcom-control-plane', label: 'mail.com Parent Mailler', capability: 'provider:inspect', icon: 'Server' },
      { path: '/gmx-control-plane', label: 'gmx.com Parent Mailler', capability: 'provider:inspect', icon: 'Server' },
      { path: '/rambler-control-plane', label: 'rambler.ru Stok & Site Kullanımı', capability: 'provider:inspect', icon: 'Server' },
      { path: '/rambler-control-plane/raw', label: 'rambler.ru Hesaplar & Import', capability: 'provider:inspect', icon: 'Server' },
      { path: '/providers', label: 'Sağlayıcı Sağlığı', capability: 'provider:view_health', icon: 'Globe' },
    ],
  },
  {
    title: 'Finans',
    items: [
      { path: '/financial-reviews', label: 'Ödeme İncelemeleri', capability: 'financial:review', icon: 'Coins' },
      { path: '/pricing', label: 'Domain Fiyatlandırma', capability: 'pricing:shadow_inspect', icon: 'Coins' },
      { path: '/tier-config', label: 'Seviye Ayarları', capability: 'tier:view', icon: 'Coins' },
    ],
  },
  {
    title: 'Sistem',
    items: [
      { path: '/announcements', label: 'Duyurular', capability: 'announcement:manage', icon: 'Info' },
      { path: '/maintenance', label: 'Bakım Modu', capability: 'maintenance:view', icon: 'Settings' },
      { path: '/activity', label: 'Son Aktiviteler', capability: 'activity:read', icon: 'Clock' },
      { path: '/observability', label: 'Gözlem ve Metrikler', capability: 'observability:read', icon: 'Trend' },
      { path: '/alerts', label: 'Uyarılar', capability: 'observability:read', icon: 'Warn' },
    ],
  },
];

const COLLAPSED_STORAGE_KEY = 'kopechka.admin.sidebar.collapsed';

function readCollapsedSet(): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((s): s is string => typeof s === 'string'));
  } catch {
    return new Set();
  }
}

function writeCollapsedSet(set: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* storage disabled — ignore */
  }
}

export interface SidebarProps {
  readonly open?: boolean;
  readonly onClose?: () => void;
}

export function Sidebar({ open = false, onClose }: SidebarProps = {}): JSX.Element {
  const session = useSession();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => {
    const stored = readCollapsedSet();
    if (stored.size > 0) return stored;
    const defaults = new Set<string>();
    for (const section of SECTIONS) {
      if (section.collapsibleByDefault && section.title) defaults.add(section.title);
    }
    return defaults;
  });

  useEffect(() => {
    writeCollapsedSet(collapsed);
  }, [collapsed]);

  function toggle(title: string): void {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  }

  const asideClass = ['adm-sidebar', open ? 'open' : ''].filter(Boolean).join(' ');

  return (
    <>
      <aside className={asideClass} aria-label="Birincil gezinti">
        <div className="console-tag">
          <span className="dot" aria-hidden="true" />
          <span className="label">Operatör Konsolu</span>
        </div>
        <nav>
          {SECTIONS.map((section, idx) => {
            const visible = section.items.filter((item) => {
              if (item.capability === null) return true;
              return session.has(item.capability);
            });
            if (visible.length === 0) return null;
            const sectionKey = section.title ?? `section-${idx}`;
            const isCollapsible = Boolean(section.title) && Boolean(section.collapsibleByDefault);
            const isOpen = isCollapsible ? !collapsed.has(section.title!) : true;

            return (
              <div key={sectionKey}>
                {section.title ? (
                  isCollapsible ? (
                    <button
                      type="button"
                      className="adm-nav-section sidebar-section-toggle"
                      aria-expanded={isOpen}
                      onClick={() => toggle(section.title!)}
                    >
                      <span>{section.title}</span>
                      <span aria-hidden="true" className="sidebar-section-caret">
                        {isOpen ? '▾' : '▸'}
                      </span>
                    </button>
                  ) : (
                    <div className="adm-nav-section">{section.title}</div>
                  )
                ) : null}
                {isOpen
                  ? visible.map((item) => {
                      const IconComp = Icon[item.icon];
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          end={item.path === '/'}
                          className={({ isActive }) => `adm-nav-item${isActive ? ' active' : ''}`}
                          onClick={() => onClose?.()}
                        >
                          <IconComp />
                          <span>{item.label}</span>
                        </NavLink>
                      );
                    })
                  : null}
              </div>
            );
          })}

          <div style={{ flex: 1, minHeight: 16 }} />

          <div
            style={{
              padding: 12,
              background: 'var(--bg-sunken)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 8,
              marginTop: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <Icon.Info size={13} style={{ color: 'var(--brand-600)' }} />
              <span style={{ fontSize: 12, fontWeight: 600 }}>Operasyonel notlar</span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
              Runbook akışları SSH + audit ile yürütülür. Bu konsol durum okur, kararı sunucu verir.
            </div>
          </div>
        </nav>
      </aside>
      {open ? (
        <div className="adm-sidebar-scrim" onClick={onClose} aria-hidden="true" />
      ) : null}
    </>
  );
}
